/*
 * Copyright Evollis 2021
 * All Rights Reserved.
 *
 * All information contained herein is, and remains the property of Evollis.
 * The intellectual and technical concepts contained herein are
 * proprietary to Evollis and are protected by trade secret and copyright law.
 * Dissemination of this information or reproduction of this material
 * is STRICTLY FORBIDDEN unless prior written permission is obtained
 * from Evollis.
 */
import { TENANTS } from "../../../../constants"
import { isValidIban, loc } from "../../../../core/utils"
import { DATE_FORMAT } from "../../constants/ClientPageConstants"
import { MARIED_VALUE } from "../../constants/MaritalSituations"
import genericSchema from "../ClientPageValidator"
import moment from "moment"
import React from "react"
import * as yup from "yup"

/* Error messages */
const requiredMessage = () => loc`Required`
const dateYYYYFormatValidationMessage = () => loc`Not a valid YYYY date`
const futureEmploymentDateMessage = () => loc`Employment date cannot be in future`
const invalidBirthDateMessage = () => loc`birthDate is not valid`
const employmentDateBeforeBirthDateMessage = () => loc`Employment date cannot be before birth date`
const wrongDiffMessage = () => loc`difference between employment date and birth date must be at least 16 years`
const maximumCharactersMessage = (maximumValue) => maximumValue + " " + loc`maximum characters`
const minimumCharactersMessage = (minimalValue) => minimalValue + " " + loc`minimum characters`
const childrenContainsMessage = () => loc`number of children must be contains between 0 and 10`
const unauthorizedCharactersMessage = () => loc`Unauthorized characters`
const siretInvalidMessage = () => loc`Format SIRET invalide`
const postalCodeMessage = () => loc`Invalid postal code`
const phoneNumberMessage = () => loc`Invalid phone number`
const ibanWrongFormatMessage = () => loc`Not a valid IBAN`
const frenchIbanMessage = () => loc("french_iban")
const bicWrongFormatMessage = () => loc`Not a valid BIC`
const futureDateMessage = () => loc`This date cannot be in future`
const yearSeniorityBeforeBirthDateMessage = () => loc`Year seniority bank cannot be before birth date`
const wrongDiffMessageBankSeniorty = () =>
  loc`difference between bank seniority date and birth date must be at least 16 years`
const numberMessage = () => loc`Must be a number`
const numberIntegerMessage = () => loc`Must be a whole number`
const mustBePositiveMessage = () => loc`Must be positive`

const invalideDateErrorMessage = () => loc`Not a valid date`
const birthDateMinMessage = () => loc`Minimum age must be > 18 years old`
const birthDateMaxessage = () => loc`Maximum age must be < 110 years old`

const futureResidenceDateMessage = () => loc`This date cannot be in future`
const residenceDateBeforeBirthDateMessage = () => loc`Residence date cannot be before birth date`
const emailNotValidMessage = () => loc`Email invalid`
const bplsEmailNotValidMessage = () => loc("bpls_invalid_email")
const bplsMustBeDifferentEmailMessage = () => loc("bpls_different_email", { paragraph: <DifferentEmailMessage /> })

const bplsMustBeDifferentPhoneMessage = () => loc("bpls_different_phone", { paragraph: <DifferentPhoneMessage /> })

const DifferentEmailMessage = () => {
  return <strong>Merci de renseigner une adresse email différente pour chaque signataire du contrat</strong>
}
const DifferentPhoneMessage = () => {
  return <strong>Merci de renseigner un téléphone différent pour chaque signataire du contrat</strong>
}
const bankAccountOwnerWrongFormat = () => loc`Wrong bank account owner format`
const regexMessage = () => loc`Unauthorized characters`
const minimumCharacterRegex = (minValue) => "^.{" + minValue + ",}$"
const maximumCaracterRegex = (maxValue) => "^.{0," + maxValue + "}$"

// regex
const defaultRegex =
  /^[a-zA-ZêëéèçÊËÉÈÇàâäÀÂ' ÄùûüÙÛÜìïîÌÏÎòôöÒÔÖ]+(([ -][a-zA-Z êëéèçÊËÉÈÇàâäÀÂÄùûüÙÛÜìïîÌÏÎòôöÒÔÖ])?[a-zA-ZêëéèçÊËÉÈÇàâäÀÂÄùûüÙÛÜìïîÌÏÎòôöÒÔÖ]*)*$/
const postalCodeRegex = /^[0-9]{4,5}$/
const siretRegex = /^[0-9]{14,14}/
const bplsPhoneRegexForNumberPhoneField = /^33(6|7){1}[0-9]{8}$/
const phoneRegex = /^\+33(6|7){1}[0-9]{8}$/
const phoneRegexFr = /^0(6|7){1}[0-9]{8}$/
const bicRegex = /^[A-Z]{6,6}[A-Z2-9][A-NP-Z0-9]([A-Z0-9]{3,3}){0,1}$/
const emailRegex = /^[a-zA-Z0-9_\-.]+@[a-zA-Z0-9_\-.]+\.[a-zA-Z0-9_\-.]+$/
const bplsEmailRegex = /^[a-zA-Z0-9_\-.+%]+@[a-zA-Z0-9.-]+\.[a-zA-Z0-9_\-.]{2,11}$/
const ibanStartWithFrRegex = /^(fr|FR|fR|Fr)/
const onlyAlphaAndAccentsAndAuthorizedCharactersRegex =
  /^[a-zA-ZêëéèçÊËÉÈÇàâäÀÂÄùûüÙÛÜìïîÌÏÎòôöÒÔÖ]+[a-zA-ZêëéèçÊËÉÈÇàâäÀÂ' ÄùûüÙÛÜìïîÌÏÎòôöÒÔÖ' -]*[a-zA-ZêëéèçÊËÉÈÇàâäÀÂÄùûüÙÛÜìïîÌÏÎòôöÒÔÖ]$/ // \u00C0-\u00FF matches a single character in the range between À (index 192) and ÿ (index 255) (case sensitive) + check si pas de whitespace ou hyphen en début ou fin.
const bankAccountOwnerRegex = /[a-zA-Z]+([1-9])*/

const checkIfSameEmailThanOthers = (optionContext) => {
  const pathValuesIndex = optionContext?.path.replace(/\D/g, "")
  const signatories = [...optionContext?.context?.signatories]
  const allSignatoriesEmailExceptCurrentOne = signatories
    .filter((_signatory, index) => index !== Number(pathValuesIndex))
    .map((signatory) => signatory.email)
  const currentSignatoryPathEmail = signatories[pathValuesIndex].email

  const isSameEmailThanOthers = (emailList, currentPathEmail) => {
    return emailList.includes(currentPathEmail)
  }
  return isSameEmailThanOthers(allSignatoriesEmailExceptCurrentOne, currentSignatoryPathEmail)
}

const checkIfSamePhoneThanOthers = (optionContext) => {
  const pathValuesIndex = optionContext?.path.replace(/\D/g, "")
  const signatories = [...optionContext?.context?.signatories]
  const allSignatoriesPhoneExceptCurrentOne = signatories
    .filter((_signatory, index) => index !== Number(pathValuesIndex))
    .map((signatory) => signatory.phoneNumber)
  const currentSignatoryPathPhone = signatories[pathValuesIndex].phoneNumber

  const isSamePhoneThanOthers = (emailList, currentPathPhone) => {
    return emailList.includes(currentPathPhone)
  }
  return isSamePhoneThanOthers(allSignatoriesPhoneExceptCurrentOne, currentSignatoryPathPhone)
}

let schema = yup.object().shape({
  identity: yup.object().shape({
    name: yup
      .string()
      .nullable()
      .required(requiredMessage)
      .min(2, minimumCharactersMessage(2))
      .max(32, maximumCharactersMessage(32))
      .matches(defaultRegex, unauthorizedCharactersMessage()),
    firstName: yup
      .string()
      .nullable()
      .min(2, minimumCharactersMessage(2))
      .max(32, maximumCharactersMessage(32))
      .required(requiredMessage)
      .matches(defaultRegex, unauthorizedCharactersMessage()),
    citizenShipCountry: yup.string().nullable().required(requiredMessage),
    birthDate: yup
      .string()
      .nullable()
      .test({
        name: "ageTest",
        message: "",
        test(value) {
          if (!value) {
            return this.createError({
              message: requiredMessage,
              path: "identity.birthDate"
            })
          }
          const birthDate = moment(value, DATE_FORMAT.identity.birthDate.FR, true)
          if (!birthDate.isValid() || birthDate.year() < 1000 || birthDate.year() > 9999) {
            return this.createError({
              message: invalideDateErrorMessage,
              path: "identity.birthDate"
            })
          }
          let currentDate = moment()
          if (!(currentDate.diff(birthDate, "years") >= 18)) {
            return this.createError({
              message: birthDateMinMessage,
              path: "identity.birthDate"
            })
          }
          if (!(currentDate.diff(birthDate, "years") < 110)) {
            return this.createError({
              message: birthDateMaxessage,
              path: "identity.birthDate"
            })
          }
        }
      })
  }),
  coordinates: yup.object().shape({
    postalCode: yup.string().nullable().required(requiredMessage).matches(postalCodeRegex, postalCodeMessage()),
    phoneNumber: yup
      .string()
      .nullable()
      .required(requiredMessage)
      .test({
        name: "phone verification",
        message: "",
        test(value) {
          const phoneValueWithoutSpaces = value?.replace(/\s/g, "")
          if (!value) {
            return this.createError({
              message: requiredMessage,
              path: "coordinates.phoneNumber"
            })
          }
          if (!phoneValueWithoutSpaces?.match(phoneRegex) && !phoneValueWithoutSpaces?.match(phoneRegexFr)) {
            return this.createError({
              message: phoneNumberMessage,
              path: "coordinates.phoneNumber"
            })
          }
        }
      }),
    email: yup
      .string()
      .nullable()
      .required(requiredMessage)
      .test({
        name: "email",
        message: "",
        test(value) {
          if (!value?.match(emailRegex)) {
            return this.createError({
              message: emailNotValidMessage,
              path: "coordinates.email"
            })
          }
        }
      }),
    iban: yup
      .string()
      .nullable()
      .test({
        name: "iban format verification",
        message: "",
        test(value) {
          if (!value) {
            return this.createError({
              message: requiredMessage,
              path: "coordinates.iban"
            })
          }
          let formatedBic = value?.toString().split(" ").join("")
          if (!isValidIban(formatedBic)) {
            return this.createError({
              message: ibanWrongFormatMessage,
              path: "coordinates.iban"
            })
          }
        }
      }),
    bic: yup
      .string()
      .nullable()
      .required(requiredMessage)
      .max(11, maximumCharactersMessage(11))
      .test({
        name: "bic format verification",
        message: "",
        test(value) {
          if (value && !value.match(bicRegex)) {
            return this.createError({
              message: bicWrongFormatMessage,
              path: "coordinates.bic"
            })
          }
        }
      })
      .required(requiredMessage),
    yearBankSeniority: yup
      .string()
      .nullable()
      .test({
        name: "yearbankseniority verification",
        message: "",
        test(value) {
          if (!value) {
            return this.createError({
              message: requiredMessage,
              path: "coordinates.yearBankSeniority"
            })
          }
          const yearbankseniority = moment(value, DATE_FORMAT.coordinates.yearBankSeniority.FR, true)
          if (!yearbankseniority.isValid() || yearbankseniority.year() < 1000 || yearbankseniority.year() > 9999) {
            return this.createError({
              message: dateYYYYFormatValidationMessage,
              path: "coordinates.yearBankSeniority"
            })
          }
          // date must not be in future
          if (yearbankseniority.isAfter(moment())) {
            return this.createError({
              message: futureDateMessage,
              path: "coordinates.yearBankSeniority"
            })
          }

          // birthDate must be valid
          const birthDate = moment(this.options.context.identity.birthDate, DATE_FORMAT.identity.birthDate.FR, true)
          if (!this.options.context.identity.birthDate || !birthDate.isValid()) {
            return this.createError({
              message: invalidBirthDateMessage,
              path: "coordinates.yearBankSeniority"
            })
          }

          //birthDate must be before bank seniority Date
          if (birthDate.isAfter(yearbankseniority)) {
            return this.createError({
              message: yearSeniorityBeforeBirthDateMessage,
              path: "coordinates.yearBankSeniority"
            })
          }

          //diff between birthDate and bank seniority date must be > 16 years
          const diff = yearbankseniority.year() - birthDate.year()
          if (diff < 16) {
            return this.createError({
              message: wrongDiffMessageBankSeniorty,
              path: "coordinates.yearBankSeniority"
            })
          }
        }
      })
  }),
  situation: yup.object().shape({
    profession: yup
      .string()
      .nullable()
      .required(requiredMessage)
      .test({
        name: "profession verification",
        message: "",
        test(value) {
          if (this.options.context.situation.showProfession && !value) {
            return this.createError({
              message: requiredMessage,
              path: "situation.profession"
            })
          }
        }
      }),
    employmentDate: yup
      .string()
      .nullable()
      .test({
        name: "employment date verification",
        message: "",
        test(value) {
          if (!value) {
            return this.createError({
              message: requiredMessage,
              path: "situation.employmentDate"
            })
          }
          const employmentDate = moment(value, DATE_FORMAT.situation.employmentDate.FR, true)
          if (!employmentDate.isValid() || employmentDate.year() < 1000 || employmentDate.year() > 9999) {
            return this.createError({
              message: dateYYYYFormatValidationMessage,
              path: "situation.employmentDate"
            })
          }
          if (employmentDate.isAfter(moment())) {
            return this.createError({
              message: futureEmploymentDateMessage,
              path: "situation.employmentDate"
            })
          }
          const birthDate = moment(this.options.context.identity.birthDate, DATE_FORMAT.identity.birthDate.FR, true)
          if (!this.options.context.identity.birthDate) {
            return this.createError({
              message: invalidBirthDateMessage,
              path: "situation.employmentDate"
            })
          }
          if (!birthDate.isValid()) {
            return this.createError({
              message: invalidBirthDateMessage,
              path: "situation.employmentDate"
            })
          }
          if (birthDate.isAfter(employmentDate)) {
            return this.createError({
              message: employmentDateBeforeBirthDateMessage,
              path: "situation.employmentDate"
            })
          }
          const diff = employmentDate.year() - birthDate.year()
          if (diff < 16) {
            return this.createError({
              message: wrongDiffMessage,
              path: "situation.employmentDate"
            })
          }
        }
      }),
    dependentChildrenNumber: yup
      .number()
      .nullable()
      .required(requiredMessage)
      .min(0, childrenContainsMessage())
      .max(10, childrenContainsMessage()),
    residenceDate: yup
      .string()
      .nullable()
      .test({
        name: "residenceDate verification",
        message: "",
        test(value) {
          if (!value) {
            return this.createError({
              message: requiredMessage,
              path: "situation.residenceDate"
            })
          }
          const residenceDate = moment(
            value,
            DATE_FORMAT.situation.residenceDate[this.options.context.countryLanguage],
            true
          )
          if (
            !residenceDate.isValid() ||
            (residenceDate.isValid() && (residenceDate.year() < 1000 || residenceDate.year() > 9999))
          ) {
            return this.createError({
              message: dateYYYYFormatValidationMessage,
              path: "situation.residenceDate"
            })
          }
          if (residenceDate.isAfter(moment())) {
            return this.createError({
              message: futureResidenceDateMessage,
              path: "situation.residenceDate"
            })
          }
          const birthDate = moment(
            this.options.context.identity.birthDate,
            DATE_FORMAT.identity.birthDate[this.options.context.countryLanguage],
            true
          )
          if (!this.options.context.identity.birthDate || !birthDate.isValid()) {
            return this.createError({
              message: invalidBirthDateMessage,
              path: "situation.residenceDate"
            })
          }
          if (birthDate.isAfter(residenceDate)) {
            return this.createError({
              message: residenceDateBeforeBirthDateMessage,
              path: "situation.residenceDate"
            })
          }
          //diff between birthDate and residenceDate must be > 16 years
          const diff = residenceDate.year() - birthDate.year()
          if (diff < 16) {
            return this.createError({
              message: wrongDiffMessage,
              path: "situation.residenceDate"
            })
          }
        }
      }),
    workingSituation: yup.string().nullable().required(requiredMessage),
    maidenName: yup
      .string()
      .nullable()
      .test({
        name: "maritalStatus",
        message: "",
        test(value) {
          const { maritalStatus } = this.options.context.situation
          if (!maritalStatus || maritalStatus.value !== MARIED_VALUE) return true

          if (!value)
            return this.createError({
              message: requiredMessage,
              path: "situation.maidenName"
            })

          if (!value.match(defaultRegex)) {
            return this.createError({
              message: regexMessage,
              path: "situation.maidenName"
            })
          }

          if (!value.match(minimumCharacterRegex(2)))
            return this.createError({
              message: minimumCharactersMessage(2),
              path: "situation.maidenName"
            })

          if (!value.match(maximumCaracterRegex(32)))
            return this.createError({
              message: maximumCharactersMessage(32),
              path: "situation.maidenName"
            })

          return true
        }
      })
  }),
  budget: yup.object().shape({
    netMonthlyIncome: yup.number().required(requiredMessage).nullable(),
    otherIncome: yup
      .number(numberMessage)
      .typeError(numberMessage)
      .nullable()
      .integer(numberIntegerMessage)
      .required(requiredMessage)
      .moreThan(-1, mustBePositiveMessage),
    rent: yup
      .number(numberMessage)
      .typeError(numberMessage)
      .nullable()
      .integer(numberIntegerMessage)
      .required(requiredMessage)
      .moreThan(-1, mustBePositiveMessage),
    otherCharges: yup
      .number(numberMessage)
      .typeError(numberMessage)
      .nullable()
      .integer(numberIntegerMessage)
      .required(requiredMessage)
      .moreThan(-1, mustBePositiveMessage),
    numberMonthsIncome: yup.object().nullable().required(requiredMessage)
  }),
  company: yup.object().shape({
    name: yup.string().required(requiredMessage).nullable(),
    siret: yup
      .string()
      .required(requiredMessage)
      .nullable()
      .test({
        name: "siret",
        message: "",
        test(value) {
          if (!value?.match(siretRegex)) {
            return this.createError({
              message: siretInvalidMessage,
              path: "company.siret"
            })
          }
        }
      }),
    executiveLastname: yup
      .string()
      .required(requiredMessage)
      .min(2, minimumCharactersMessage(2))
      .max(32, maximumCharactersMessage(32))
      .nullable()
      .test({
        name: "executive lastname",
        message: "",
        test(value) {
          if (!value) {
            return this.createError({
              message: requiredMessage,
              path: `${this.options.path}`
            })
          }
          if (
            this.options?.context?.tenant === TENANTS.BPLS &&
            value.length >= 2 &&
            !onlyAlphaAndAccentsAndAuthorizedCharactersRegex.test(value)
          ) {
            return this.createError({
              message: unauthorizedCharactersMessage,
              path: `${this.options.path}`
            })
          }
        }
      }),
    executiveFirstname: yup
      .string()
      .required(requiredMessage)
      .min(2, minimumCharactersMessage(2))
      .max(32, maximumCharactersMessage(32))
      .nullable()
      .test({
        name: "executive firstname",
        message: "",
        test(value) {
          if (!value) {
            return this.createError({
              message: requiredMessage,
              path: `${this.options.path}`
            })
          }
          if (
            this.options?.context?.tenant === TENANTS.BPLS &&
            value.length >= 2 &&
            !onlyAlphaAndAccentsAndAuthorizedCharactersRegex.test(value)
          ) {
            return this.createError({
              message: unauthorizedCharactersMessage,
              path: `${this.options.path}`
            })
          }
        }
      }),
    executiveBirthdate: yup
      .string()
      .nullable()
      .test({
        name: "executiveBirthdate",
        message: "",
        test(value) {
          if (!value) {
            return this.createError({
              message: requiredMessage,
              path: "company.executiveBirthdate"
            })
          }

          const currentDate = moment()
          const birthDate = moment(value, DATE_FORMAT.identity.birthDate.FR, true)
          if (
            !birthDate.isValid() ||
            currentDate.diff(birthDate, "years") <= 18 ||
            birthDate > currentDate ||
            birthDate.year() < 1901
          ) {
            return this.createError({
              message: invalideDateErrorMessage,
              path: `${this.options.path}`
            })
          }
        }
      })
  }),
  signatories: yup.array().of(
    yup.object().shape({
      title: yup
        .string()
        .required(requiredMessage)
        .nullable()
        .test({
          name: "title",
          message: "",
          test(value) {
            if (!value) {
              return this.createError({
                message: requiredMessage,
                path: `${this.options.path}`
              })
            }
          }
        }),
      name: yup
        .string()
        .required(requiredMessage)
        .min(2, minimumCharactersMessage(2))
        .max(32, maximumCharactersMessage(32))
        .nullable()
        .test({
          name: "signatory name",
          message: "",
          test(value) {
            if (!value) {
              return this.createError({
                message: requiredMessage,
                path: `${this.options.path}`
              })
            }
            if (
              this.options?.context?.tenant === TENANTS.BPLS &&
              value.length >= 2 &&
              !onlyAlphaAndAccentsAndAuthorizedCharactersRegex.test(value)
            ) {
              return this.createError({
                message: unauthorizedCharactersMessage,
                path: `${this.options.path}`
              })
            }
          }
        }),
      firstName: yup
        .string()
        .required(requiredMessage)
        .min(2, minimumCharactersMessage(2))
        .max(32, maximumCharactersMessage(32))
        .nullable()
        .test({
          name: "signatory firstName",
          message: "",
          test(value) {
            if (!value) {
              return this.createError({
                message: requiredMessage,
                path: `${this.options.path}`
              })
            }
            if (
              this.options?.context?.tenant === TENANTS.BPLS &&
              value.length >= 2 &&
              !onlyAlphaAndAccentsAndAuthorizedCharactersRegex.test(value)
            ) {
              return this.createError({
                message: unauthorizedCharactersMessage,
                path: `${this.options.path}`
              })
            }
          }
        }),
      email: yup
        .string()
        .nullable()
        .required(requiredMessage)
        .test({
          name: "signatory email",
          message: "",
          test(value) {
            if (!value) {
              return this.createError({
                message: requiredMessage,
                path: `${this.options.path}`
              })
            }
            if (this.options?.context?.tenant === TENANTS.BPLS && !value?.match(bplsEmailRegex)) {
              return this.createError({
                message: bplsEmailNotValidMessage,
                path: `${this.options.path}`
              })
            }
            if (this.options?.context?.tenant === TENANTS.BPLS && checkIfSameEmailThanOthers(this.options)) {
              return this.createError({
                message: bplsMustBeDifferentEmailMessage,
                path: `${this.options.path}`
              })
            }
          }
        }),
      phoneNumber: yup
        .string()
        .nullable()
        .required(requiredMessage)
        .test({
          name: "signatory phone verification",
          message: "",
          test(value) {
            if (!value) {
              return this.createError({
                message: requiredMessage,
                path: `${this.options.path}`
              })
            }
            if (!value?.match(bplsPhoneRegexForNumberPhoneField)) {
              return this.createError({
                message: phoneNumberMessage,
                path: `${this.options.path}`
              })
            }
            if (this.options?.context?.tenant === TENANTS.BPLS && checkIfSamePhoneThanOthers(this.options)) {
              return this.createError({
                message: bplsMustBeDifferentPhoneMessage,
                path: `${this.options.path}`
              })
            }
          }
        }),
      signatoryBirthdate: yup
        .string()
        .required(requiredMessage)
        .nullable()
        .test({
          name: "signatory birthdate",
          message: "",
          test(value) {
            if (!value) {
              return this.createError({
                message: requiredMessage,
                path: `${this.options.path}`
              })
            }
            const currentDate = moment()
            const birthDate = moment(value, DATE_FORMAT.identity.birthDate.FR, true)
            if (
              !birthDate.isValid() ||
              currentDate.diff(birthDate, "years") <= 18 ||
              birthDate > currentDate ||
              birthDate.year() < 1901
            ) {
              return this.createError({
                message: invalideDateErrorMessage,
                path: `${this.options.path}`
              })
            }
          }
        })
    })
  ),
  bankCoordinates: yup.object().shape({
    ownerName: yup
      .string()
      .required(requiredMessage)
      .nullable()
      .test({
        name: "Bank owner name format verification",
        message: "",
        test(value) {
          if (!value) {
            return this.createError({
              message: requiredMessage,
              path: "bankCoordinates.ownerName"
            })
          }
          if (!bankAccountOwnerRegex.test(value)) {
            return this.createError({
              message: bankAccountOwnerWrongFormat,
              path: "bankCoordinates.ownerName"
            })
          }
          return true
        }
      }),
    iban: yup
      .string()
      .nullable()
      .test({
        name: "iban format verification",
        message: "",
        test(value) {
          if (!value) {
            return this.createError({
              message: requiredMessage,
              path: "bankCoordinates.iban"
            })
          }
          if (!ibanStartWithFrRegex.test(value)) {
            return this.createError({
              message: frenchIbanMessage,
              path: "bankCoordinates.iban"
            })
          }
          let formatedBic = value?.toString().split(" ").join("")
          if (!isValidIban(formatedBic)) {
            return this.createError({
              message: ibanWrongFormatMessage,
              path: "bankCoordinates.iban"
            })
          }
        }
      })
  }),
  myAddresses: yup.object().shape({
    shippingAddress: yup.string().nullable().required(requiredMessage),
    shippingAddressComplement: yup.string().nullable(),
    zipShippingAddress: yup.string().nullable().required(requiredMessage).matches(postalCodeRegex, postalCodeMessage()),
    cityShippingAddress: yup.string().nullable().required(requiredMessage),
    billingAddress: yup.string().nullable().required(requiredMessage),
    billingAddressComplement: yup.string().nullable(),
    zipBillingAddress: yup.string().nullable().required(requiredMessage).matches(postalCodeRegex, postalCodeMessage()),
    cityBillingAddress: yup.string().nullable().required(requiredMessage)
  })
})

export default (values) => schema.concat(genericSchema).validate(values, { abortEarly: false, context: values })
